import React, {createContext, useContext, useState, useCallback, useMemo, useEffect} from 'react';
import type {
    CartItem,
    CartLine,
    CustomKitLine,
    CuratedRecLine,
    MembershipLine,
    ProductLine,
    StorefrontProduct,
} from '../types';
import {customKitLineKey, productLineKey} from '../types';
import {useSubscription} from '@/modules/patient-dashboard/hooks/useTestsQuery';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import type {UserInfo} from '@/modules/auth/hooks/useAuth/context';

interface CartContextValue {
    items: CartItem[];
    /** Add a real storefront product. Increments qty if already present. */
    addItem: (product: StorefrontProduct) => void;
    /**
     * Add a synthetic line (custom kit, curated rec, or membership). Replaces an
     * existing line with the same `key` rather than incrementing — qty
     * for synthetic lines is always 1.
     */
    addLine: (line: CustomKitLine | CuratedRecLine | MembershipLine) => void;
    removeItem: (lineKey: string) => void;
    updateQuantity: (lineKey: string, quantity: number) => void;
    clearCart: () => void;
    itemCount: number;
    subtotalCents: number;
    /** True when any line in the cart bills on a recurring cadence. */
    hasRecurring: boolean;
    isDrawerOpen: boolean;
    openDrawer: () => void;
    closeDrawer: () => void;
    toggleDrawer: () => void;
    /** True when the patient has an active My Labs Plus membership. */
    isMember: boolean;
    /** True when active membership or a membership in this cart unlocks member prices. */
    usesMemberPricing: boolean;
}

const CartContext = createContext<CartContextValue | null>(null);

const LEGACY_STORAGE_KEY = 'mld_cart';
const STORAGE_KEY_PREFIX = 'mld_cart';

type CartStorageScope = {
    key: string;
    sessionOnly: boolean;
};

/**
 * Cart state is browser-local, but it still belongs to one effective patient.
 * Admin impersonation changes `userInfo.id`, so including that id prevents a
 * cart assembled for patient A from appearing while viewing patient B.
 */
function cartStorageScope(user?: UserInfo): CartStorageScope {
    if (!user?.isLoggedIn) {
        return {key: `${STORAGE_KEY_PREFIX}:guest`, sessionOnly: false};
    }

    const rawUserId = user.userInfo?.id;
    const userId =
        rawUserId == null || String(rawUserId).trim() === '' ? 'pending' : encodeURIComponent(String(rawUserId).trim());
    return {
        key: `${STORAGE_KEY_PREFIX}:user:${userId}`,
        // Impersonation auth is tab-scoped; its cart should be too.
        sessionOnly: Boolean(user.sessionOnly),
    };
}

function scopeStorage(scope: CartStorageScope): Storage {
    return scope.sessionOnly ? sessionStorage : localStorage;
}

function loadCart(scope: CartStorageScope): CartItem[] {
    try {
        const raw = scopeStorage(scope).getItem(scope.key);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        // Defensive: drop any pre-refactor entries that don't have the
        // new discriminated `line` field. They'd crash the renderer.
        return Array.isArray(parsed) ? parsed.filter((i) => i && typeof i === 'object' && i.line && i.line.kind) : [];
    } catch {
        return [];
    }
}

function saveCart(scope: CartStorageScope, items: CartItem[]) {
    try {
        scopeStorage(scope).setItem(scope.key, JSON.stringify(items));
    } catch {
        // A blocked/full browser store should not break shopping in memory.
    }
}

function removeLegacyUnscopedCart() {
    try {
        localStorage.removeItem(LEGACY_STORAGE_KEY);
        sessionStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
        // Ignore unavailable browser storage.
    }
}

function productLineFor(product: StorefrontProduct): ProductLine {
    return {
        kind: 'product',
        key: productLineKey(product.id),
        name: product.name,
        description: product.description,
        category: product.category,
        image_url: product.image_url,
        price_cents: product.price_cents,
        member_price_cents: product.member_price_cents,
        product,
    };
}

export const CartProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
    const {user} = useAuth();
    const requestedScope = useMemo(
        () => cartStorageScope(user),
        [user?.isLoggedIn, user?.sessionOnly, user?.userInfo?.id]
    );
    const [activeScope, setActiveScope] = useState<CartStorageScope>(requestedScope);
    const [items, setItems] = useState<CartItem[]>(() => loadCart(requestedScope));
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const isMember = useSubscription();

    const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
    const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);
    const toggleDrawer = useCallback(() => setIsDrawerOpen((prev) => !prev), []);

    useEffect(() => {
        removeLegacyUnscopedCart();
    }, []);

    useEffect(() => {
        if (activeScope.key === requestedScope.key && activeScope.sessionOnly === requestedScope.sessionOnly) {
            return;
        }

        setItems(loadCart(requestedScope));
        setIsDrawerOpen(false);
        setActiveScope(requestedScope);
    }, [activeScope.key, activeScope.sessionOnly, requestedScope]);

    useEffect(() => {
        saveCart(activeScope, items);
    }, [activeScope, items]);

    const addItem = useCallback((product: StorefrontProduct) => {
        setItems((prev) => {
            const key = productLineKey(product.id);
            const existing = prev.find((i) => i.line.key === key);
            if (existing) {
                return prev.map((i) => (i.line.key === key ? {...i, quantity: i.quantity + 1} : i));
            }
            return [...prev, {line: productLineFor(product), quantity: 1}];
        });
        setIsDrawerOpen(true);
    }, []);

    const addLine = useCallback((line: CustomKitLine | CuratedRecLine | MembershipLine) => {
        setItems((prev) => {
            const idx = prev.findIndex((i) => i.line.key === line.key);
            if (idx >= 0) {
                // Replace in place — synthetic lines re-snapshot fresh
                // metadata (assay_codes, prices) every time the user
                // hits the button.
                const next = prev.slice();
                next[idx] = {line, quantity: 1};
                return next;
            }
            // Custom kit is exclusive between modes — if user adds
            // member, drop any pre-existing one_time entry, and vice
            // versa. Two custom-kit lines in the same cart would mean
            // charging for the same assembly twice.
            let cleaned = prev;
            if (line.kind === 'custom_kit') {
                cleaned = prev.filter((i) => i.line.kind !== 'custom_kit');
            } else if (line.kind === 'membership') {
                // Only one membership plan can be pending in the cart.
                cleaned = prev.filter((i) => i.line.kind !== 'membership');
            }
            return [...cleaned, {line, quantity: 1}];
        });
        setIsDrawerOpen(true);
    }, []);

    const removeItem = useCallback((lineKey: string) => {
        setItems((prev) => prev.filter((i) => i.line.key !== lineKey));
    }, []);

    const updateQuantity = useCallback((lineKey: string, quantity: number) => {
        if (quantity < 1) return;
        setItems((prev) =>
            prev.map((i) => {
                if (i.line.key !== lineKey) return i;
                // Synthetic lines are always qty 1 — ignore updates.
                if (i.line.kind !== 'product') return i;
                return {...i, quantity};
            })
        );
    }, []);

    const clearCart = useCallback(() => {
        setItems([]);
    }, []);

    const itemCount = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items]);

    const usesMemberPricing = useMemo(() => cartUsesMemberPricing(items, isMember), [items, isMember]);

    const subtotalCents = useMemo(
        () => items.reduce((sum, i) => sum + lineCharge(i.line, usesMemberPricing) * i.quantity, 0),
        [items, usesMemberPricing]
    );

    const hasRecurring = useMemo(() => items.some((i) => i.line.recurring != null), [items]);

    const value = useMemo(
        () => ({
            items,
            addItem,
            addLine,
            removeItem,
            updateQuantity,
            clearCart,
            itemCount,
            subtotalCents,
            hasRecurring,
            isDrawerOpen,
            openDrawer,
            closeDrawer,
            toggleDrawer,
            isMember,
            usesMemberPricing,
        }),
        [
            items,
            addItem,
            addLine,
            removeItem,
            updateQuantity,
            clearCart,
            itemCount,
            subtotalCents,
            hasRecurring,
            isDrawerOpen,
            openDrawer,
            closeDrawer,
            toggleDrawer,
            isMember,
            usesMemberPricing,
        ]
    );

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

/**
 * What the cart actually charges for a line. Curated recs may have
 * `price_cents` set to the kit MSRP for display, but the patient owes
 * `patient_amount_cents` after the employer cost-split.
 *
 * Curated recs are never member-discounted: the enrollment checkout
 * (`finalizeBioMatchCheckout`) always charges `patient_amount_cents`
 * server-side regardless of membership, so the cart must match that —
 * no member branch here for `curated_rec`, even if a line somehow
 * carries a `member_price_cents` (older cached cart entries).
 *
 * For `product` and `custom_kit` lines, when `isMember` is true and
 * the line carries a snapshotted `member_price_cents`, that discounted
 * amount wins over the regular charge — for both one-time and
 * subscription (`recurring`) lines. Those checkout paths ARE
 * member-aware server-side.
 */
export function lineCharge(line: CartLine, isMember = false): number {
    if (line.kind === 'curated_rec') return line.patient_amount_cents;
    if (isMember && line.member_price_cents != null) return line.member_price_cents;
    return line.price_cents;
}

/**
 * Member prices take effect immediately when the patient either already has
 * an active membership or is purchasing one in this cart. Removing the
 * pending membership returns a non-member cart to regular pricing.
 */
export function cartUsesMemberPricing(items: CartItem[], isMember: boolean): boolean {
    return isMember || items.some(({line}) => line.kind === 'membership');
}

/**
 * Whole-number percent a non-member would save on the current cart by
 * joining My Labs Plus, aggregated across every line that carries a
 * `member_price_cents`. Powers the cart's "Join My Labs+ to save"
 * nudge. Returns 0 when there's nothing to save (empty cart, or no
 * line has a member price below its regular charge).
 */
export function memberSavingsPct(items: CartItem[]): number {
    const regular = items.reduce((sum, i) => sum + lineCharge(i.line, false) * i.quantity, 0);
    if (regular <= 0) return 0;
    const member = items.reduce((sum, i) => sum + lineCharge(i.line, true) * i.quantity, 0);
    return Math.max(0, Math.round((1 - member / regular) * 100));
}

/** Both cart JOIN treatments use this predicate so their visibility can never
 * diverge. Membership status comes from the active, unexpired server state. */
export function shouldShowMembershipJoinOffers(isMember: boolean): boolean {
    return !isMember;
}

export function useCart(): CartContextValue {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error('useCart must be used within CartProvider');
    return ctx;
}

// Re-exports kept for backward compat with imports that pulled the
// helpers from the context module previously.
export {customKitLineKey, productLineKey};
